// src/components/GestionBillets.js
import React, { useState, useEffect, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  ajouterBillets, getBilletsOnce, getParametresOnce,
  getStatutOnce, ecouterStatistiques, initialiserPrefixe,
  modifierVente, supprimerVente,
} from "../services/db";
import { useTombola } from "../contexts/TombolaContext";
import { creerSessionPaiement } from "../services/stripe";
import { verifierQuota80 } from "../services/brevo";
import QuotaBar from "./QuotaBar";
import { useAuth } from "../contexts/AuthContext";

import { useProfil } from "../contexts/ProfilContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Montant au format français : 8 → « 8,00 € »
function euros(n) {
  return `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function grouperParVente(billets) {
  const ventes = {};
  billets.forEach((b) => {
    const key = b.venteId || `solo_${b.id}`;
    if (!ventes[key]) {
      ventes[key] = {
        venteId: key, nom: b.nom, telephone: b.telephone,
        email: b.email, methodeEncaissement: b.methodeEncaissement,
        saisiPar: b.saisiPar, venteAt: b.venteAt || null,
        quantite: 0, numeros: [], gagnant: false,
        modifiePar: b.modifiePar,
      };
    }
    ventes[key].quantite++;
    ventes[key].numeros.push(b.numero);
    if (b.gagnant) ventes[key].gagnant = true;
  });
  return Object.values(ventes).sort((a, b) => {
    if (!a.venteAt) return 1;
    if (!b.venteAt) return -1;
    return new Date(b.venteAt) - new Date(a.venteAt);
  });
}

// ─── Confettis ────────────────────────────────────────────────────────────────
function Confetti({ active }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const pieces = Array.from({ length: 100 }, () => ({
      x: Math.random() * canvas.width, y: -20,
      w: Math.random() * 10 + 5, h: Math.random() * 6 + 3,
      color: ["#667eea","#764ba2","#ffc107","#4caf50","#ff5722","#00bcd4"][Math.floor(Math.random()*6)],
      speed: Math.random()*4+2, angle: Math.random()*360, spin: Math.random()*6-3, drift: Math.random()*2-1,
    }));
    let frame = 0;
    function draw() {
      ctx.clearRect(0,0,canvas.width,canvas.height);
      pieces.forEach((p) => {
        p.y+=p.speed; p.x+=p.drift; p.angle+=p.spin;
        ctx.save(); ctx.translate(p.x,p.y); ctx.rotate((p.angle*Math.PI)/180);
        ctx.fillStyle=p.color; ctx.fillRect(-p.w/2,-p.h/2,p.w,p.h);
        ctx.restore();
      });
      frame++;
      if(frame<120) animRef.current=requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [active]);
  if (!active) return null;
  return <canvas ref={canvasRef} style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:9999}} />;
}

// ─── Envoi du lien de preuve d'achat par SMS (depuis le téléphone du bénévole) ─
// Ouvre l'appli SMS avec le numéro et le message pré-remplis : 0 € pour TomboLive.
function lienSMS(telephone, numeros, tombolaNom, url) {
  const numero = String(telephone || "").replace(/[^\d+]/g, "");
  if (numero.length < 6) return null;
  const billets = numeros.length > 1
    ? `vos billets ${numeros[0]} à ${numeros[numeros.length - 1]}`
    : `votre billet ${numeros[0]}`;
  const texte = `Merci pour votre achat ! Voici ${billets}${tombolaNom ? ` pour la tombola « ${tombolaNom} »` : ""} : ${url}`;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return `sms:${numero}${ios ? "&" : "?"}body=${encodeURIComponent(texte)}`;
}

const avertSMS = { fontSize:"0.7rem", color:"#999", marginTop:"4px", lineHeight:1.4, textAlign:"center" };
const btnSMS = { display:"block", width:"100%", boxSizing:"border-box", padding:"0.6rem", background:"linear-gradient(135deg,#2e7d32,#43a047)", color:"#fff", borderRadius:"8px", textDecoration:"none", fontWeight:"700", fontSize:"0.88rem", marginTop:"8px", textAlign:"center" };

// ─── Modal QR Code ────────────────────────────────────────────────────────────
function QRModal({ vente, orgId, tombolaId, tombolaNom, onClose }) {
  const [copied, setCopied] = React.useState(false);
  if (!vente) return null;
  const url = `${window.location.origin}/confirmation/${vente.venteId}/${orgId}/${tombolaId}`;
  const sms = lienSMS(vente.telephone, vente.numeros, tombolaNom, url);
  return (
    <div style={qrs.overlay} onClick={onClose}>
      <div style={qrs.modal} onClick={(e) => e.stopPropagation()}>
        <div style={qrs.nom}>{vente.nom}</div>
        <div style={qrs.billets}>{vente.quantite} billet{vente.quantite>1?"s":""} — {vente.numeros[0]}{vente.numeros.length>1?` à ${vente.numeros[vente.numeros.length-1]}`:""}</div>
        <QRCodeSVG value={url} size={200} level="M" fgColor="#1a3a8f" bgColor="#ffffff" style={{borderRadius:"8px",margin:"0.75rem 0"}} />
        <div style={qrs.hint}>L'acheteur scanne ce QR pour sa preuve d'achat</div>
        <button onClick={() => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(()=>setCopied(false),2500); }}
          style={qrs.copyBtn}>{copied?"✅ Copié !":"📋 Copier le lien"}</button>
        {sms && <>
          <a href={sms} style={btnSMS}>💬 Envoyer par SMS</a>
          <div style={{ ...avertSMS, marginBottom:"8px" }}>Facultatif : le SMS part de votre téléphone, l'acheteur verra votre numéro.</div>
        </>}
        <button onClick={onClose} style={qrs.btn}>Fermer</button>
      </div>
    </div>
  );
}
const qrs = {
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:3000},
  modal:{background:"#fff",borderRadius:"20px",padding:"1.75rem 2rem",textAlign:"center",maxWidth:"320px",width:"90%"},
  nom:{fontWeight:"800",color:"#1a1a2e",fontSize:"1.1rem",marginBottom:"4px"},
  billets:{fontSize:"0.82rem",color:"#667eea",fontWeight:"600"},
  hint:{fontSize:"0.75rem",color:"#aaa",marginBottom:"8px"},
  copyBtn:{display:"block",width:"100%",padding:"0.55rem",background:"#eef0ff",color:"#4c5ee8",border:"1px solid #c7d2ff",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.85rem",marginBottom:"8px"},
  btn:{padding:"0.6rem 1.5rem",background:"#f5f5f5",border:"1px solid #ddd",borderRadius:"8px",cursor:"pointer",fontWeight:"600"},
};

// ─── Modal modification ───────────────────────────────────────────────────────
function ModalModif({ vente, params, onSave, onClose, peutChangerMethode }) {
  const [form, setForm] = useState({ nom:vente.nom||"", telephone:vente.telephone||"", email:vente.email||"", methodeEncaissement:vente.methodeEncaissement||"Espèces" });
  return (
    <div style={ms.overlay}>
      <div style={ms.modal}>
        <h3 style={ms.title}>✏️ Modifier la vente</h3>
        <div style={ms.info}>{vente.numeros.length} billet{vente.numeros.length>1?"s":""} — {vente.numeros[0]}{vente.numeros.length>1?` à ${vente.numeros[vente.numeros.length-1]}`:""}</div>
        {["nom","telephone","email"].map((f)=>(
          <div key={f} style={ms.field}>
            <label style={ms.label}>{f==="nom"?"Nom":f==="telephone"?"Téléphone":"Email"}</label>
            <input type={f==="email"?"email":f==="telephone"?"tel":"text"} value={form[f]}
              onChange={(e)=>setForm({...form,[f]:e.target.value})} style={ms.input} />
          </div>
        ))}
        <div style={ms.field}>
          <label style={ms.label}>Méthode</label>
          {!peutChangerMethode ? (
            <div style={{...ms.input,background:"#f7f7f7",color:"#777"}}>{form.methodeEncaissement} <span style={{fontSize:"0.75rem"}}>(modifiable par le responsable)</span></div>
          ) : (
          <select value={form.methodeEncaissement} onChange={(e)=>setForm({...form,methodeEncaissement:e.target.value})} style={ms.input}>
            {(params.methodesEncaissement||["Espèces","Carte Bancaire","Chèque"]).map((m)=>(
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          )}
        </div>
        <div style={ms.actions}>
          <button onClick={onClose} style={ms.btnCancel}>Annuler</button>
          <button onClick={()=>onSave(form)} style={ms.btnSave}>💾 Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
const ms = {
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000},
  modal:{background:"#fff",borderRadius:"16px",padding:"1.75rem",maxWidth:"420px",width:"92%"},
  title:{margin:"0 0 0.75rem",fontSize:"1rem",fontWeight:"700",color:"#1a1a2e"},
  info:{background:"#eef0ff",color:"#4c5ee8",borderRadius:"8px",padding:"6px 12px",fontSize:"0.85rem",fontWeight:"600",marginBottom:"1rem"},
  field:{marginBottom:"0.85rem"},
  label:{display:"block",fontSize:"0.8rem",fontWeight:"600",color:"#555",marginBottom:"4px"},
  input:{width:"100%",padding:"0.6rem 0.85rem",border:"1.5px solid #e0e0e0",borderRadius:"8px",fontSize:"0.92rem",outline:"none",boxSizing:"border-box"},
  actions:{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"1rem"},
  btnCancel:{padding:"0.6rem 1.25rem",background:"#f5f5f5",border:"1px solid #ddd",borderRadius:"8px",cursor:"pointer",fontWeight:"600"},
  btnSave:{padding:"0.6rem 1.25rem",background:"#667eea",color:"#fff",border:"none",borderRadius:"8px",cursor:"pointer",fontWeight:"700"},
};

// ─── Ligne de vente ───────────────────────────────────────────────────────────
// Un bénévole peut corriger une faute de frappe sur SA vente pendant 15 minutes,
// tant que les ventes sont ouvertes et que le billet n'est pas gagnant.
// Le reste (méthode d'encaissement, suppression, ventes des autres) : responsables uniquement.
const DELAI_CORRECTION_MIN = 15;
function correctionPossible(vente, isMine, ventesOuvertes) {
  if (!isMine || !ventesOuvertes || vente.gagnant || !vente.venteAt) return false;
  return Date.now() - new Date(vente.venteAt).getTime() < DELAI_CORRECTION_MIN * 60 * 1000;
}

function VenteRow({ vente, prixTicket, isAdmin, isMine, ventesOuvertes, onModif, onSupprimer, onQR, showBenevole, getPseudo }) {
  const peutModifier = isAdmin || correctionPossible(vente, isMine, ventesOuvertes);
  const total = (vente.quantite * prixTicket).toFixed(2);
  const heure = vente.venteAt ? new Date(vente.venteAt).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}) : "—";
  const pseudo = getPseudo ? getPseudo(vente.saisiPar) : vente.saisiPar?.split("@")[0];
  return (
    <div style={{...vr.row,...(vente.gagnant?vr.rowGagnant:{})}}>
      <div style={vr.left}>
        <div style={vr.nom}>
          {vente.nom}
          {vente.gagnant && <span style={vr.tagGagnant}>🏆</span>}
          {!isMine && <span style={vr.tagOther}>{pseudo}</span>}
        </div>
        <div style={vr.meta}>
          <span style={vr.billets}>{vente.quantite} billet{vente.quantite>1?"s":""} — {vente.numeros[0]}{vente.numeros.length>1?` à ${vente.numeros[vente.numeros.length-1]}`:""}</span>
          <span style={vr.sep}>·</span>
          <span style={vr.methode}>{vente.methodeEncaissement}</span>
          <span style={vr.sep}>·</span>
          <span style={vr.heure}>{heure}</span>
          {showBenevole && pseudo && <><span style={vr.sep}>·</span><span style={vr.benevole}>{pseudo}</span></>}
        </div>
      </div>
      <div style={vr.right}>
        <span style={vr.total}>{euros(total)}</span>
        <div style={vr.actions}>
          {vente.venteId && !vente.venteId.startsWith("solo_") && (
            <button onClick={()=>onQR(vente)} style={vr.btnQR} title="QR Code">📱</button>
          )}
          {peutModifier && <button onClick={()=>onModif(vente)} style={vr.btnEdit}>✏️</button>}
          {isAdmin && <button onClick={()=>onSupprimer(vente)} style={vr.btnDel}>🗑️</button>}
        </div>
      </div>
    </div>
  );
}
const vr = {
  row:{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px",background:"#fff",border:"1px solid #eee",borderRadius:"10px",padding:"10px 12px",marginBottom:"6px"},
  rowGagnant:{border:"1.5px solid #ffc107",background:"#fffdf0"},
  left:{flex:1,minWidth:0},
  nom:{fontWeight:"700",color:"#1a1a2e",fontSize:"0.92rem",display:"flex",alignItems:"center",gap:"6px",flexWrap:"wrap"},
  tagGagnant:{background:"#ffc107",color:"#fff",fontSize:"0.7rem",padding:"1px 6px",borderRadius:"10px",fontWeight:"700"},
  tagOther:{background:"#f3e8ff",color:"#6b21a8",fontSize:"0.7rem",padding:"1px 6px",borderRadius:"10px",fontWeight:"600"},
  meta:{display:"flex",alignItems:"center",gap:"5px",flexWrap:"wrap",marginTop:"3px"},
  billets:{fontSize:"0.78rem",color:"#667eea",fontWeight:"600"},
  sep:{color:"#ddd",fontSize:"0.7rem"},
  methode:{fontSize:"0.75rem",color:"#888"},
  heure:{fontSize:"0.75rem",color:"#aaa"},
  benevole:{fontSize:"0.75rem",color:"#764ba2",fontWeight:"600"},
  right:{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:"4px"},
  total:{fontWeight:"800",color:"#2e7d32",fontSize:"0.95rem"},
  actions:{display:"flex",gap:"4px"},
  btnQR:{background:"none",border:"1px solid #c7d2ff",borderRadius:"6px",padding:"3px 7px",cursor:"pointer",fontSize:"0.8rem"},
  btnEdit:{background:"none",border:"1px solid #e0e0e0",borderRadius:"6px",padding:"3px 7px",cursor:"pointer",fontSize:"0.8rem"},
  btnDel:{background:"none",border:"1px solid #ffcdd2",borderRadius:"6px",padding:"3px 7px",cursor:"pointer",fontSize:"0.8rem"},
};

// ─── Composant principal ──────────────────────────────────────────────────────
export default function GestionBillets() {
  const { activeTombolaId, activeTombola, checkQuota, isPro, STARTER_LIMIT, isAdminTombola } = useTombola();
  const { currentUser, orgId, isAdmin: isOrgAdmin } = useAuth();
  // Responsable = admin de l'association ou admin de cette tombola
  const isAdmin = isOrgAdmin || isAdminTombola;
  useProfil();

  const [params, setParams] = useState({ prixTicket:1, methodesEncaissement:["Espèces","Carte Bancaire","Chèque"] });
  const [billets, setBillets] = useState([]);
  const [statut, setStatut] = useState({ ventesClosees:false });
  const [stats, setStats] = useState({ totalBillets:0, totalVentes:0 });
  const [monPrefixe, setMonPrefixe] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [loadingPaiement, setLoadingPaiement] = useState(false);

  const [form, setForm] = useState({ nom:"", telephone:"", email:"", quantite:1, methodeEncaissement:"" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [confetti, setConfetti] = useState(false);
  const [error, setError] = useState("");
  const [venteAModifier, setVenteAModifier] = useState(null);
  const [qrVente, setQrVente] = useState(null);
  const [copiedConfirm, setCopiedConfirm] = useState(false);
  const [msg, setMsg] = useState("");
  const [vue, setVue] = useState("saisie");
  const [search, setSearch] = useState("");

  const isArchived = activeTombola?.archived;

  async function handlePaiement() {
    setLoadingPaiement(true);
    try {
      const { url } = await creerSessionPaiement({
        orgId,
        tombolaId: activeTombolaId,
        tombolaName: activeTombola?.nom || "Tombola",
      });
      window.location.href = url;
    } catch (e) {
      setError("Erreur lors de la redirection vers le paiement.");
    }
    setLoadingPaiement(false);
  }

  async function chargerDonnees() {
    if (!orgId || !activeTombolaId) return;
    setRefreshing(true);
    try {
      const [p, b, st, prefixe] = await Promise.all([
        getParametresOnce(orgId, activeTombolaId),
        getBilletsOnce(orgId, activeTombolaId),
        getStatutOnce(orgId, activeTombolaId),
        initialiserPrefixe(orgId, activeTombolaId),
      ]);
      setParams(p);
      setForm((f) => ({ ...f, methodeEncaissement: p.methodesEncaissement?.[0] || "Espèces" }));
      setBillets(b);
      setStatut(st);
      setMonPrefixe(prefixe);
    } catch (e) { console.error(e); }
    setRefreshing(false);
  }

  useEffect(() => {
    chargerDonnees();
    if (!orgId || !activeTombolaId) return;
    const unsub = ecouterStatistiques(orgId, activeTombolaId, setStats);
    return unsub;
  }, [orgId, activeTombolaId]);

  function showMsg(text) { setMsg(text); setTimeout(() => setMsg(""), 3000); }

  const toutes = grouperParVente(billets);
  const mesVentes = toutes.filter((v) => v.saisiPar === currentUser?.email);
  const dernieresVentes = mesVentes.slice(0, 3);
  const totalBillets = stats.totalBillets || billets.length;

  const ventesBase = toutes;
  const ventesFiltrees = search.trim()
    ? ventesBase.filter((v) =>
        v.nom.toLowerCase().includes(search.toLowerCase()) ||
        (v.telephone||"").includes(search) ||
        v.numeros.some((n) => String(n).includes(search))
      )
    : ventesBase;

  const tirageTermine = false;
  const ventesClosees = statut?.ventesClosees || tirageTermine;

  function validate() {
    if (!form.nom.trim()) return "Le nom est obligatoire.";
    if (!form.telephone?.trim() && !form.email?.trim()) return "Indiquez un téléphone ou un email : c'est ce qui permet de prévenir l'acheteur s'il gagne.";
    if (form.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Email invalide.";
    if (Number(form.quantite) < 1) return "Quantité invalide.";
    return null;
  }

  async function handleAjouter(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    // Vérification quota Freemium
    const quota = checkQuota(totalBillets + Number(form.quantite));
    if (!quota.ok) { setError(quota.message); return; }

    setError(""); setLoading(true);
    try {
      const { numeros, venteId } = await ajouterBillets(
        orgId, activeTombolaId,
        { ...form, quantite: Number(form.quantite) },
        currentUser?.email, monPrefixe
      );
      const total = (numeros.length * params.prixTicket).toFixed(2);
      const confirmUrl = `${window.location.origin}/confirmation/${venteId}/${orgId}/${activeTombolaId}`;
      setConfirmation({
        nom: form.nom.trim(), numeros, total, methode: form.methodeEncaissement, venteId, confirmUrl,
        sms: lienSMS(form.telephone, numeros, activeTombola?.nom, confirmUrl),
      });
      setConfetti(true);
      setTimeout(() => setConfetti(false), 2500);
      setForm((f) => ({ ...f, nom:"", telephone:"", email:"", quantite:1 }));

      // Notification email si quota 80 atteint (seulement en Starter)
      const nouveauTotal = totalBillets + numeros.length;
      if (!isPro && totalBillets < STARTER_LIMIT && nouveauTotal >= 80) {
        // Le serveur recompte, vérifie le plan et prévient l'admin (une seule fois)
        verifierQuota80({ orgId, tombolaId: activeTombolaId }).catch(() => {});
      }

      // Mise à jour locale
      setBillets((prev) => [...prev, ...numeros.map((n, i) => ({
        id: `local_${venteId}_${i}`, numero: n, nom: form.nom.trim(),
        telephone: form.telephone||"", email: form.email||"",
        methodeEncaissement: form.methodeEncaissement,
        saisiPar: currentUser?.email, venteId, venteAt: new Date().toISOString(),
        quantiteVente: numeros.length, gagnant: false,
      }))]);
    } catch { setError("Erreur lors de l'ajout. Réessayez."); }
    setLoading(false);
  }

  async function handleSaveModif(formData) {
    try {
      await modifierVente(orgId, activeTombolaId, venteAModifier.venteId, formData, currentUser?.email);
      showMsg("✅ Vente modifiée.");
      chargerDonnees();
    } catch (e) {
      showMsg(e?.code === "permission-denied"
        ? `❌ Correction impossible après ${DELAI_CORRECTION_MIN} min : demandez au responsable.`
        : "❌ Erreur.");
    }
    setVenteAModifier(null);
  }

  async function handleSupprimer(vente) {
    if (!window.confirm(`Supprimer la vente de ${vente.quantite} billet(s) pour "${vente.nom}" ?`)) return;
    try {
      await supprimerVente(orgId, activeTombolaId, vente.venteId);
      showMsg("🗑️ Vente supprimée.");
      chargerDonnees();
    } catch (e) { showMsg(`❌ ${e?.message?.includes("gagnant") ? e.message : "Erreur."}`); }
  }

  const montantTotal = (Number(form.quantite) * params.prixTicket).toFixed(2);
  return (
    <div style={s.page}>
      <Confetti active={confetti} />
      <QRModal vente={qrVente} orgId={orgId} tombolaId={activeTombolaId} tombolaNom={activeTombola?.nom} onClose={() => setQrVente(null)} />

      {venteAModifier && (
        <ModalModif vente={venteAModifier} params={params} peutChangerMethode={isAdmin}
          onSave={handleSaveModif} onClose={() => setVenteAModifier(null)} />
      )}

      {/* Confirmation */}
      {confirmation && (
        <div style={s.confirmOverlay} onClick={() => setConfirmation(null)}>
          <div style={s.confirmCard} onClick={(e) => e.stopPropagation()}>
            <div style={s.confirmEmoji}>✅</div>
            <div style={s.confirmNom}>{confirmation.nom}</div>
            <div style={s.confirmBillets}>
              {confirmation.numeros.length>1
                ?`Billets ${confirmation.numeros[0]} à ${confirmation.numeros[confirmation.numeros.length-1]}`
                :`Billet ${confirmation.numeros[0]}`}
            </div>
            <div style={s.confirmMontant}>{euros(confirmation.total)}</div>
            <div style={s.confirmMethode}>{confirmation.methode}</div>
            <div style={s.qrWrapper}>
              <QRCodeSVG value={confirmation.confirmUrl} size={160} level="M"
                fgColor="#1a3a8f" bgColor="#ffffff" style={{borderRadius:"8px"}} />
              <div style={s.qrLabel}>L'acheteur scanne ce QR code</div>
              <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(confirmation.confirmUrl); setCopiedConfirm(true); setTimeout(()=>setCopiedConfirm(false),2500); }}
                style={s.confirmCopyBtn}>{copiedConfirm?"✅ Copié !":"📋 Copier le lien"}</button>
              {confirmation.sms && (
                <>
                  <a href={confirmation.sms} onClick={(e) => e.stopPropagation()} style={btnSMS}>
                    💬 Envoyer par SMS à l'acheteur
                  </a>
                  <div style={avertSMS}>Facultatif : le SMS part de votre téléphone, l'acheteur verra votre numéro.</div>
                </>
              )}
            </div>
            <div style={s.confirmHint}>Appuyez ailleurs pour continuer</div>
          </div>
        </div>
      )}

      {isArchived && <div style={s.banner}>📦 Tombola archivée — consultation seule.</div>}
      {ventesClosees && !isArchived && (
        <div style={s.closedBanner}>🔴 Ventes clôturées par l'administrateur.</div>
      )}

      {/* Barre de progression quota */}
      {!isArchived && !ventesClosees && (
        <QuotaBar
          totalBillets={totalBillets}
          isPro={isPro}
          STARTER_LIMIT={STARTER_LIMIT}
          onUpgrade={handlePaiement}
          peutPayer={isAdmin}
          loadingPaiement={loadingPaiement}
        />
      )}

      {/* Compteurs */}
      <div style={s.compteurs}>
        <div style={s.compteur}>
          <span style={s.compteurNum}>{totalBillets}</span>
          <span style={s.compteurLabel}>Total billets</span>
        </div>
        <div style={s.compteurDivider} />
        <div style={s.compteur}>
          <span style={{...s.compteurNum,color:"#2e7d32"}}>{mesVentes.reduce((a,v)=>a+v.quantite,0)}</span>
          <span style={s.compteurLabel}>Mes billets</span>
        </div>
        <div style={s.compteurDivider} />
        <div style={s.compteur}>
          <span style={{...s.compteurNum,color:"#764ba2"}}>{monPrefixe}</span>
          <span style={s.compteurLabel}>Mon préfixe</span>
        </div>
      </div>

      {/* Onglets */}
      <div style={s.tabs}>
        <button onClick={() => setVue("saisie")} style={{...s.tab,...(vue==="saisie"?s.tabActive:{})}}>✚ Saisie</button>
        <button onClick={() => { setVue("historique"); setSearch(""); }} style={{...s.tab,...(vue==="historique"?s.tabActive:{})}}>📋 Historique</button>
      </div>

      {msg && <div style={s.msgBox}>{msg}</div>}

      {/* ── SAISIE ── */}
      {vue === "saisie" && (
        <>
          {!isArchived && !ventesClosees && (
            <form onSubmit={handleAjouter} style={s.form}>
              <div style={s.fieldGroup}>
                <label style={s.label}>👤 Nom de l'acheteur</label>
                <input type="text" placeholder="Jean Dupont"
                  value={form.nom} onChange={(e) => setForm({...form,nom:e.target.value})}
                  required style={s.inputLarge} autoComplete="off" />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>🎟️ Nombre de billets</label>
                <div style={s.quantiteRow}>
                  <button type="button" style={s.qBtn} onClick={() => setForm((f)=>({...f,quantite:Math.max(1,Number(f.quantite)-1)}))}>−</button>
                  <input type="number" min="1" max="500" value={form.quantite}
                    onChange={(e) => setForm((f)=>({...f,quantite:Math.max(1,Number(e.target.value)||1)}))}
                    style={s.qInput} />
                  <button type="button" style={s.qBtn} onClick={() => setForm((f)=>({...f,quantite:Number(f.quantite)+1}))}>+</button>
                </div>
              </div>
              <div style={s.montantBox}>
                <span style={s.montantLabel}>À encaisser</span>
                <span style={s.montantValeur}>{euros(montantTotal)}</span>
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>💳 Méthode d'encaissement</label>
                <div style={s.methodesGrid}>
                  {(params.methodesEncaissement||["Espèces","Carte Bancaire","Chèque"]).map((m)=>(
                    <button key={m} type="button"
                      onClick={()=>setForm({...form,methodeEncaissement:m})}
                      style={{...s.methodeBtn,...(form.methodeEncaissement===m?s.methodeBtnActive:{})}}>
                      {m==="Espèces"?"💵":m==="Carte Bancaire"?"💳":m==="Chèque"?"📝":"💰"} {m}
                    </button>
                  ))}
                </div>
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>📞 Contact <span style={s.labelHint}>(téléphone ou email, pour prévenir l'acheteur s'il gagne)</span></label>
                <input type="tel" placeholder="Téléphone" value={form.telephone}
                  onChange={(e)=>setForm({...form,telephone:e.target.value})} style={s.input} />
                <input type="email" placeholder="Email" value={form.email}
                  onChange={(e)=>setForm({...form,email:e.target.value})}
                  style={{...s.input,marginTop:"8px"}} />
              </div>
              {error && <div style={s.errBox}>⚠️ {error}</div>}
              <button type="submit" disabled={loading} style={s.btnSubmit}>
                {loading?"Enregistrement…":`✚ Ajouter ${Number(form.quantite)>1?form.quantite+" billets":"le billet"}`}
              </button>
            </form>
          )}

          {/* 3 dernières ventes */}
          {dernieresVentes.length > 0 && (
            <div style={{...s.dernieres,marginTop:"1.5rem"}}>
              <div style={s.dernieresTitle}>🕐 Mes 3 dernières ventes</div>
              {dernieresVentes.map((v) => (
                <VenteRow key={v.venteId} vente={v} prixTicket={params.prixTicket}
                  isAdmin={isAdmin} isMine={true} ventesOuvertes={!ventesClosees && !isArchived}
                  onModif={setVenteAModifier} onSupprimer={handleSupprimer}
                  onQR={setQrVente} showBenevole={false} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── HISTORIQUE ── */}
      {vue === "historique" && (
        <div>
          <div style={s.refreshRow}>
            <button onClick={chargerDonnees} disabled={refreshing} style={s.btnRefresh}>
              {refreshing?"⏳":"🔄"} Actualiser
            </button>
          </div>
          <input type="text" placeholder="🔍 Rechercher nom, téléphone, n° billet…"
            value={search} onChange={(e) => setSearch(e.target.value)} style={s.searchBar} />
          <div style={s.resumeRow}>
            <span style={s.resumeStat}><strong>{ventesFiltrees.length}</strong> vente(s)</span>
            <span style={s.resumeStat}><strong>{ventesFiltrees.reduce((a,v)=>a+v.quantite,0)}</strong> billet(s)</span>
            <span style={{...s.resumeStat,color:"#2e7d32",fontWeight:"700"}}>
              {euros(ventesFiltrees.reduce((a,v)=>a+v.quantite,0)*params.prixTicket)}
            </span>
          </div>
          {ventesFiltrees.length === 0 ? (
            <div style={s.empty}>{search?"Aucun résultat.":"Aucune vente enregistrée."}</div>
          ) : (
            ventesFiltrees.map((v) => (
              <VenteRow key={v.venteId} vente={v} prixTicket={params.prixTicket}
                isAdmin={isAdmin} isMine={v.saisiPar === currentUser?.email} ventesOuvertes={!ventesClosees && !isArchived}
                onModif={setVenteAModifier} onSupprimer={handleSupprimer}
                onQR={setQrVente} showBenevole={true} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  page:{padding:"0",maxWidth:"520px",margin:"0 auto"},
  banner:{background:"#fff8e1",border:"1px solid #ffe082",borderRadius:"8px",padding:"0.65rem 1rem",marginBottom:"1rem",color:"#e65100",fontSize:"0.88rem",fontWeight:"600"},
  closedBanner:{background:"#fce4ec",border:"1px solid #f48fb1",borderRadius:"8px",padding:"0.65rem 1rem",marginBottom:"1rem",color:"#c62828",fontSize:"0.88rem",fontWeight:"700",textAlign:"center"},
  quotaWarning:{background:"#fff3e0",border:"1px solid #ffcc02",borderRadius:"8px",padding:"0.65rem 1rem",marginBottom:"1rem",color:"#e65100",fontSize:"0.85rem"},
  confirmOverlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000},
  confirmCard:{background:"#fff",borderRadius:"24px",padding:"2rem",textAlign:"center",maxWidth:"320px",width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"},
  confirmEmoji:{fontSize:"3rem",marginBottom:"0.25rem"},
  confirmNom:{fontSize:"1.4rem",fontWeight:"800",color:"#1a1a2e",marginBottom:"0.25rem"},
  confirmBillets:{fontSize:"0.9rem",color:"#667eea",fontWeight:"600",marginBottom:"0.5rem"},
  confirmMontant:{fontSize:"2.75rem",fontWeight:"900",color:"#2e7d32",letterSpacing:"-1px",marginBottom:"0.1rem"},
  confirmMethode:{fontSize:"0.88rem",color:"#888",marginBottom:"0.75rem"},
  qrWrapper:{display:"flex",flexDirection:"column",alignItems:"center",gap:"6px",margin:"0.5rem 0"},
  qrLabel:{fontSize:"0.75rem",color:"#aaa"},
  confirmCopyBtn:{padding:"0.5rem 1.25rem",background:"#eef0ff",color:"#4c5ee8",border:"1px solid #c7d2ff",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.82rem",marginTop:"6px",width:"100%"},
  confirmHint:{fontSize:"0.72rem",color:"#ccc",fontStyle:"italic",marginTop:"0.5rem"},
  compteurs:{display:"flex",alignItems:"center",background:"#f7f8fc",borderRadius:"14px",border:"1.5px solid #e8e8e8",padding:"1rem 1.5rem",marginBottom:"1rem",justifyContent:"space-around"},
  compteur:{textAlign:"center"},
  compteurNum:{display:"block",fontSize:"2rem",fontWeight:"900",color:"#1a1a2e",letterSpacing:"-1px"},
  compteurLabel:{fontSize:"0.75rem",color:"#888",fontWeight:"600",textTransform:"uppercase",letterSpacing:"0.5px"},
  compteurDivider:{width:"1px",height:"40px",background:"#e0e0e0"},
  tabs:{display:"flex",gap:"6px",marginBottom:"1rem"},
  tab:{flex:1,padding:"0.7rem",background:"#f7f8fc",border:"1.5px solid #e0e0e0",borderRadius:"10px",cursor:"pointer",fontWeight:"600",color:"#888",fontSize:"0.88rem"},
  tabActive:{background:"#eef0ff",border:"1.5px solid #667eea",color:"#667eea"},
  msgBox:{background:"#e8f5e9",color:"#1b5e20",borderRadius:"8px",padding:"0.6rem 1rem",marginBottom:"1rem",fontSize:"0.88rem"},
  dernieres:{background:"#f7f8fc",borderRadius:"12px",border:"1.5px solid #e8e8e8",padding:"0.85rem 1rem"},
  dernieresTitle:{fontSize:"0.78rem",fontWeight:"700",color:"#888",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"0.6rem"},
  form:{display:"flex",flexDirection:"column",gap:"1.25rem"},
  fieldGroup:{display:"flex",flexDirection:"column",gap:"8px"},
  label:{fontSize:"0.88rem",fontWeight:"700",color:"#444"},
  labelHint:{fontWeight:"400",color:"#aaa",fontSize:"0.78rem"},
  inputLarge:{padding:"0.85rem 1rem",border:"2px solid #e0e0e0",borderRadius:"12px",fontSize:"1.1rem",outline:"none",width:"100%",boxSizing:"border-box"},
  input:{padding:"0.7rem 1rem",border:"2px solid #e0e0e0",borderRadius:"12px",fontSize:"1rem",outline:"none",width:"100%",boxSizing:"border-box"},
  quantiteRow:{display:"flex",alignItems:"center",background:"#f7f8fc",borderRadius:"14px",border:"2px solid #e0e0e0",overflow:"hidden",width:"fit-content"},
  qBtn:{width:"56px",height:"56px",background:"none",border:"none",fontSize:"1.75rem",cursor:"pointer",color:"#667eea",display:"flex",alignItems:"center",justifyContent:"center"},
  qInput:{fontSize:"1.6rem",fontWeight:"800",color:"#1a1a2e",width:"72px",textAlign:"center",border:"none",outline:"none",background:"transparent"},
  montantBox:{display:"flex",alignItems:"center",justifyContent:"space-between",background:"linear-gradient(135deg,#e8f5e9,#f1f8e9)",borderRadius:"14px",padding:"1rem 1.25rem",border:"2px solid #a5d6a7"},
  montantLabel:{fontSize:"0.88rem",fontWeight:"700",color:"#2e7d32",textTransform:"uppercase",letterSpacing:"0.5px"},
  montantValeur:{fontSize:"2.25rem",fontWeight:"900",color:"#2e7d32",letterSpacing:"-1px"},
  methodesGrid:{display:"flex",flexWrap:"wrap",gap:"8px"},
  methodeBtn:{flex:1,minWidth:"100px",padding:"0.75rem 0.5rem",background:"#f7f8fc",border:"2px solid #e0e0e0",borderRadius:"12px",fontSize:"0.88rem",fontWeight:"600",cursor:"pointer",color:"#555",textAlign:"center"},
  methodeBtnActive:{background:"#eef0ff",border:"2px solid #667eea",color:"#667eea"},
  errBox:{background:"#ffebee",color:"#b71c1c",borderRadius:"10px",padding:"0.75rem 1rem",fontSize:"0.88rem"},
  btnSubmit:{padding:"1.1rem",background:"linear-gradient(135deg,#667eea,#764ba2)",color:"#fff",border:"none",borderRadius:"14px",fontSize:"1.1rem",fontWeight:"800",cursor:"pointer"},
  refreshRow:{display:"flex",gap:"10px",marginBottom:"0.75rem"},
  btnRefresh:{padding:"0.5rem 1rem",background:"#f0f4ff",color:"#4c5ee8",border:"1px solid #c7d2ff",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.82rem"},
  searchBar:{width:"100%",padding:"0.65rem 1rem",border:"1.5px solid #e0e0e0",borderRadius:"10px",fontSize:"0.92rem",outline:"none",marginBottom:"0.85rem",boxSizing:"border-box"},
  resumeRow:{display:"flex",gap:"12px",background:"#f7f8fc",borderRadius:"10px",padding:"0.75rem 1rem",marginBottom:"1rem"},
  resumeStat:{flex:1,textAlign:"center",fontSize:"0.88rem",color:"#555"},
  empty:{textAlign:"center",color:"#aaa",padding:"2rem",fontStyle:"italic"},
};
